import React, { useEffect, useState } from "react";
import TagIcon from "@mui/icons-material/Tag";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { format } from "date-fns";
import PolygonCard from "../../atoms/polygonCard/PolygonCard";
import DatePickerField from "../../atoms/datePickerField/DatePickerField";
import ImpactResults from "../impactResults/ImpactResults";
import ImpactProgress from "../impactProgress/ImpactProgress";
import "./ImpactStep.css";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import { SEVERITY_INDICES } from "../../../conts/simulationConstants";
const ImpactStep = ({
  onNext,
  onReset = () => {},
  realPolygon = {},
  simPolygon = {},
  onDraw,
  onImport,
  onSelectHistoric,
  impacts = {},
  onLaunchAnalysis,
}) => {
  const [simDate, setSimDate] = useState(new Date());
  const [reference, setReference] = useState("");
  const [submitted, setSubmitted] = useState(false); // true after a launch attempt
  const [inputsOpen, setInputsOpen] = useState(true);
  const [severity, setSeverity] = useState("");
  const severityLabel = (code) => {
    const s = SEVERITY_INDICES.find((x) => x.value === code);
    return s ? `${s.label} (${s.value})` : "—";
  };
  const locked = Boolean(impacts.isLoading);
  const results = impacts.data?.results ?? null;

  // fold the form automatically when new results arrive
  useEffect(() => {
    if (results) setInputsOpen(false);
  }, [impacts.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmtArea = (p) =>
    p?.feature && p.area != null ? `${Number(p.area).toFixed(1)} ha` : "—";

  const errors = {
    reference: !reference.trim(),
    severity: !severity,
    date: !simDate,
    real: !realPolygon.feature,
    sim: !simPolygon.feature,
  };
  const labels = {
    reference: "la référence",
    severity: "l’indice de sévérité",
    date: "la date de simulation",
    real: "le polygone sinistré",
    sim: "le polygone menacé",
  };
  const missing = Object.keys(errors).filter((k) => errors[k]);
  const hasErr = (key) => submitted && errors[key];
  const validationError =
    submitted && missing.length > 0
      ? `Veuillez renseigner : ${missing.map((k) => labels[k]).join(", ")}.`
      : null;

  const handleLaunch = () => {
    setSubmitted(true);
    if (missing.length > 0) return;
    onLaunchAnalysis({
      reference: reference.trim(),
      severity,
      fireDate: format(simDate, "yyyy-MM-dd"),
    });
  };

  const handleReset = () => {
    setReference("");
    setSeverity("");
    setSimDate(new Date());
    setSubmitted(false);
    setInputsOpen(true);
    onReset();
  };

  return (
    <div className="impact-step">
      <div className="impact-step__body">
        <header className="impact-step__header">
          <h2>Analyse d’impact</h2>
          <button
            type="button"
            className="impact-step__toggle"
            onClick={() => setInputsOpen((o) => !o)}
            aria-expanded={inputsOpen}
            aria-controls="impact-inputs"
          >
            {inputsOpen ? "Replier" : "Modifier"}
            {inputsOpen ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </button>
        </header>

        {/* compact recap, only visible when folded */}
        {!inputsOpen && (
          <button
            type="button"
            className="impact-step__summary"
            onClick={() => setInputsOpen(true)}
          >
            <span>
              <b>{reference || "—"}</b>
            </span>
            <span>Sévérité : {severityLabel(severity)}</span>
            <span>{simDate ? format(simDate, "dd/MM/yyyy") : "—"}</span>
            <span>Sinistré : {fmtArea(realPolygon)}</span>
            <span>Menacé : {fmtArea(simPolygon)}</span>
          </button>
        )}

        <div
          id="impact-inputs"
          className={`impact-step__collapse ${inputsOpen ? "" : "is-collapsed"}`}
          {...(!inputsOpen ? { inert: "" } : {})}
        >
          <div className="impact-step__collapse-inner">
            <div className="impact-step__fields">
              <label
                className={`impact-step__field ${hasErr("reference") ? "is-invalid" : ""}`}
              >
                <span>Référence</span>
                <div>
                  <TagIcon fontSize="small" />
                  <input
                    placeholder="Référence"
                    value={reference}
                    disabled={locked}
                    aria-invalid={hasErr("reference")}
                    onChange={(e) => setReference(e.target.value)}
                  />
                </div>
              </label>
<label
  className={`impact-step__field ${hasErr("severity") ? "is-invalid" : ""}`}
>
  <span>Indice de sévérité</span>
  <div>
    <SpeedOutlinedIcon fontSize="small" />
    <select
      value={severity}
      disabled={locked}
      aria-invalid={hasErr("severity")}
      className={severity ? "" : "is-placeholder"}
      onChange={(e) => setSeverity(e.target.value)}
    >
      <option value="" disabled>
        Sélectionner
      </option>
      {SEVERITY_INDICES.map((s) => (
        <option key={s.value} value={s.value}>
          {s.label} ({s.value}) · {s.hours} h
        </option>
      ))}
    </select>
    <ExpandMoreIcon fontSize="small" className="impact-step__select-chevron" />
  </div>
</label>
              <label
                className={`impact-step__field ${hasErr("date") ? "is-invalid" : ""}`}
              >
                <span>Date de simulation</span>
                <div>
                  <CalendarTodayOutlinedIcon fontSize="small" />
                  <DatePickerField
                    value={simDate}
                    onChange={setSimDate}
                    disabled={locked}
                    aria-invalid={hasErr("date")}
                  />
                </div>
              </label>
            </div>

            <div className="impact-step__polygons-row">
              <div
                className={`impact-step__polygon ${hasErr("real") ? "is-invalid" : ""}`}
              >
                <PolygonCard
                  id="real"
                  title="Polygone sinistré"
                  subtitle="Ce qui a réellement brûlé"
                  polygon={realPolygon}
                  onDraw={onDraw}
                  onImport={onImport}
                  onSelectHistoric={onSelectHistoric}
                  disabled={locked}
                />
              </div>

              <div
                className={`impact-step__polygon ${hasErr("sim") ? "is-invalid" : ""}`}
              >
                <PolygonCard
                  id="sim"
                  title="Polygone menacé"
                  subtitle="Feu en sens libre, sans intervention"
                  polygon={simPolygon}
                  onDraw={onDraw}
                  onImport={onImport}
                  onSelectHistoric={onSelectHistoric}
                  disabled={locked}
                />
              </div>
            </div>

            <button
              type="button"
              className="impact-step__btn impact-step__btn--primary impact-step__btn--launch"
              onClick={handleLaunch}
              disabled={locked}
            >
              <PlayArrowOutlinedIcon />
              {locked ? "Analyse en cours…" : "Lancer l’analyse"}
            </button>
          </div>
        </div>

        {locked && <ImpactProgress />}

        {validationError && (
          <div className="impact-step__alert" role="alert">
            <ErrorOutlineIcon className="impact-step__alert-icon" />
            <p>{validationError}</p>
          </div>
        )}

        {impacts.error && (
          <div className="impact-step__alert" role="alert">
            <ErrorOutlineIcon className="impact-step__alert-icon" />
            <p>{impacts.error}</p>
          </div>
        )}

        {results && (
          <div className="impact-step__results">
            <ImpactResults
              results={results}
              warnings={impacts.data?.warnings ?? []}
            />
          </div>
        )}
      </div>

      {results && (
        <footer className="impact-step__footer">
          <button
            type="button"
            className="impact-step__btn impact-step__btn--secondary"
            onClick={handleReset}
          >
            <RestartAltIcon fontSize="small" />
            Nouvelle analyse
          </button>
          <button
            type="button"
            className="impact-step__btn impact-step__btn--next"
            onClick={onNext}
          >
            Continuer vers le coût des sauvés
          </button>
        </footer>
      )}
    </div>
  );
};

export default ImpactStep;
